import { Banner, Button, Input, Loader, Select, Surface, Text, Textarea } from '@cloudflare/kumo'
import type { BroadcastPurpose } from '@repo/shared'
import { Megaphone } from '@phosphor-icons/react'
import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { useGetToken } from '../lib/auth'
import { fmtInt, toMessage } from '../lib/format'

/** 공지 성격 (로드맵 P6). 광고성은 수신 동의한 사람에게만 낮에 '(광고)' 표기를 붙여 푸시한다 — 서버 lib/notify.ts */
const PURPOSE_ITEMS: { label: string; value: BroadcastPurpose }[] = [
  { label: '광고성 — 새 기능·혜택·이벤트 홍보', value: 'advertising' },
  { label: '서비스 공지 — 약관 변경·장애·점검 (광고 없음)', value: 'notice' },
]

/**
 * 전체 공지 — 로그인한 적 있는 모든 사용자에게 인앱 알림 + 푸시. 받는 사람 수를 먼저 보여주고,
 * 그 수가 바뀌지 않았을 때만 발송한다 (서버가 expectedRecipients 를 확인).
 */
export function NotifyPage() {
  const getToken = useGetToken()
  const [recipients, setRecipients] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [purpose, setPurpose] = useState<BroadcastPurpose>('advertising')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const loadRecipients = useCallback(async () => {
    try {
      setRecipients((await adminApi.broadcast.recipients(getToken)).count)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    }
  }, [getToken])

  useEffect(() => {
    void loadRecipients()
  }, [loadRecipients])

  const valid = title.trim().length > 0 && body.trim().length > 0 && (recipients ?? 0) > 0

  const send = async () => {
    if (recipients === null) return
    setBusy(true)
    try {
      const r = await adminApi.broadcast.send(getToken, {
        title: title.trim(),
        body: body.trim(),
        expectedRecipients: recipients,
        purpose,
      })
      setDone(
        `${fmtInt(r.queued)}명에게 보내는 중이에요. 푸시는 순서대로 나가서 몇 분 걸릴 수 있어요.`,
      )
      setTitle('')
      setBody('')
      setPurpose('advertising')
      setConfirmOpen(false)
    } catch (err) {
      setError(toMessage(err))
      setConfirmOpen(false)
      void loadRecipients()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="공지 발송"
        description="모든 사용자에게 앱 알림과 푸시를 보내요. 한 사람에게만 보낼 때는 [사용자] 화면을 쓰세요."
      />
      <HelpPanel
        id="notify"
        steps={[
          <>제목과 내용을 쓰세요. 푸시에는 제목 한 줄과 내용 두 줄 정도만 보여요 — 짧게.</>,
          <>
            <b>보내기</b>를 누르면 받는 사람 수를 다시 확인해요. 확인하면 바로 발송돼요.
          </>,
        ]}
        notes={[
          '되돌릴 수 없어요. 오타를 꼭 확인하세요.',
          '받는 사람 = 제품을 만든 적 있거나 구독 기록이 있는 로그인 사용자. 게스트는 받지 않아요.',
          '사용자 언어에 맞춰 번역되지 않아요. 여러 언어 사용자가 섞여 있으면 영어로 쓰는 게 안전해요.',
          '광고성 공지(새 기능·혜택 홍보 포함)는 혜택·이벤트 알림에 동의한 사람에게만, 한국 시간과 받는 사람 현지 시간 모두 21시~8시가 아닐 때만 푸시가 가요. 제목 앞 (광고) 와 끝의 수신거부 안내는 자동으로 붙어요. 앱 알림 탭에는 모두에게 남아요.',
          '서비스 공지는 광고나 홍보가 전혀 없을 때만 고르세요. 홍보가 한 줄이라도 섞이면 광고성이에요(정보통신망법 제50조).',
        ]}
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}
      {done && <Banner variant="default" title="발송을 시작했어요" description={done} />}

      <Surface className="flex flex-col gap-5 rounded-lg p-6">
        <div className="flex items-center gap-2">
          {recipients === null ? (
            <Loader />
          ) : (
            <Text variant="secondary" size="sm">
              받는 사람 <b className="text-kumo-default">{fmtInt(recipients)}명</b>
            </Text>
          )}
        </div>
        <Select
          label="공지 성격"
          className="w-full"
          value={purpose}
          onValueChange={(v: string | null) =>
            setPurpose(v === 'notice' ? 'notice' : 'advertising')
          }
          items={PURPOSE_ITEMS}
        />
        <Input
          label="제목"
          maxLength={80}
          placeholder="예: 새 기능 — 공장 견적을 여러 곳에서 받아보세요"
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
        />
        <Textarea
          label="내용"
          maxLength={500}
          value={body}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value)}
        />
        <div>
          <Button
            variant="primary"
            icon={<Megaphone />}
            disabled={!valid || busy}
            onClick={() => setConfirmOpen(true)}
          >
            보내기
          </Button>
        </div>
      </Surface>

      <ConfirmDialog
        open={confirmOpen}
        title={`${fmtInt(recipients ?? 0)}명에게 보낼까요?`}
        description={
          <>
            {purpose === 'advertising'
              ? '광고성 — 동의한 사람에게만 푸시'
              : '서비스 공지 — 모두에게 푸시'}
            <br />
            <b>{title}</b>
            <br />
            {body}
            <br />
            <br />
            보낸 뒤에는 취소할 수 없어요.
          </>
        }
        confirmLabel="발송"
        busy={busy}
        onConfirm={() => void send()}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  )
}
